import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertCircle, ArrowLeft, CheckCircle2, Loader2, MailWarning } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";
import { toast } from "sonner";

const AuthShell = ({ children }: { children: React.ReactNode }) => (
  <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
    <div className="w-full max-w-md">{children}</div>
  </div>
);

export default function InvitationExpired() {
  const [searchParams] = useSearchParams();
  const workspaceId = searchParams.get("invite");
  const token = searchParams.get("token");

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [workspaceName, setWorkspaceName] = useState<string | null>(null);
  const [missingParams, setMissingParams] = useState(false);

  useEffect(() => {
    if (!workspaceId || !token) {
      setMissingParams(true);
    }
  }, [workspaceId, token]);

  const handleRequest = async () => {
    if (!workspaceId || !token) return;
    setSubmitting(true);
    setError(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke(
        "request-new-invitation",
        { body: { workspaceId, token } }
      );

      if (fnError) {
        // Try to read a structured error from the function response
        let message = "Impossible d'envoyer la demande pour le moment.";
        try {
          const ctx = (fnError as { context?: Response }).context;
          if (ctx && typeof ctx.json === "function") {
            const parsed = await ctx.json();
            if (parsed?.error) message = parsed.error;
          }
        } catch {
          /* noop */
        }
        setError(message);
        return;
      }

      if (data?.success) {
        setSubmitted(true);
        if (data.workspaceName) setWorkspaceName(data.workspaceName);
        toast.success("Demande envoyée aux administrateurs");
      } else if (data?.error) {
        setError(data.error);
      } else {
        setError("Réponse inattendue du serveur.");
      }
    } catch (err) {
      logger.error("Error requesting new invitation", err, "InvitationExpired");
      setError("Une erreur est survenue. Réessayez plus tard.");
    } finally {
      setSubmitting(false);
    }
  };

  if (missingParams) {
    return (
      <AuthShell>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Lien d'invitation invalide</AlertTitle>
          <AlertDescription>
            Le lien que vous avez utilisé est incomplet. Demandez à votre administrateur
            de vous renvoyer une invitation.
          </AlertDescription>
        </Alert>
        <div className="mt-6 text-center">
          <Button asChild variant="outline">
            <Link to="/auth">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour à la connexion
            </Link>
          </Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <Link
        to="/auth"
        className="flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Retour à la connexion
      </Link>

      <Card>
        <CardHeader className="space-y-3">
          <div className="w-12 h-12 rounded-full bg-warning/10 flex items-center justify-center">
            <MailWarning className="w-6 h-6 text-warning" />
          </div>
          <CardTitle className="text-2xl">Invitation expirée</CardTitle>
          <p className="text-muted-foreground text-sm">
            Le lien d'invitation que vous avez utilisé n'est plus valide. Pour des raisons
            de sécurité, les invitations expirent au bout de 7 jours.
          </p>
        </CardHeader>
        <CardContent className="space-y-5">
          {submitted ? (
            <Alert className="border-success/30 bg-success/5">
              <CheckCircle2 className="h-4 w-4 text-success" />
              <AlertTitle>Demande envoyée</AlertTitle>
              <AlertDescription>
                Nous avons prévenu les administrateurs
                {workspaceName ? <> de <strong>{workspaceName}</strong></> : null}.
                Vous recevrez un nouvel email d'invitation dès qu'ils l'auront renvoyée.
              </AlertDescription>
            </Alert>
          ) : (
            <>
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="rounded-lg bg-muted/50 p-4 space-y-2 text-sm text-muted-foreground">
                <p className="font-medium text-foreground">Que se passe-t-il ensuite ?</p>
                <ol className="list-decimal pl-5 space-y-1">
                  <li>Vous demandez une nouvelle invitation ci-dessous.</li>
                  <li>Les administrateurs de l'espace reçoivent un email.</li>
                  <li>Une nouvelle invitation vous est envoyée par email.</li>
                </ol>
              </div>

              <Button
                onClick={handleRequest}
                disabled={submitting}
                className="w-full"
                aria-label="Demander une nouvelle invitation"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Envoi en cours...
                  </>
                ) : (
                  "Demander une nouvelle invitation"
                )}
              </Button>
            </>
          )}

          <p className="text-xs text-center text-muted-foreground">
            Vous avez déjà un compte ?{" "}
            <Link to="/auth" className="text-primary hover:underline">
              Se connecter
            </Link>
          </p>
        </CardContent>
      </Card>
    </AuthShell>
  );
}
